import { useCallback, useEffect, type ReactNode } from 'react';
import { Card } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Link, useLocation, useOutletContext, useParams } from 'react-router-dom';
import aiContentToolsApi, {
  useGetContentOverviewQuery,
  useViewHistoryMessageQuery
} from '@/app/ContentTools/api/aiContentAnalysisApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import AiAnalysisOverview from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisOverview';
import { AiAnalysisOverviewSkeleton } from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSkeletons';
import { mapContentOverview } from '@/app/ContentTools/utils/mapContentOverview';
import { aiAnalysisHistoryPath } from '@/app/ContentTools/utils/aiAnalysisPaths';
import AiAnalysisSearchBox from '@/app/ContentTools/components/ai-content-analysis/AiAnalysisSearchBox';
import AiDetectorDetailsPanel from '@/app/ContentTools/components/ai-content-analysis/AiDetectorDetailsPanel';
import EeatAnalysisDetailsPanel from '@/app/ContentTools/components/ai-content-analysis/EeatAnalysisDetailsPanel';
import GeoAnalysisDetailsPanel from '@/app/ContentTools/components/ai-content-analysis/GeoAnalysisDetailsPanel';
import SeoAnalysisDetailsPanel from '@/app/ContentTools/components/ai-content-analysis/SeoAnalysisDetailsPanel';
import { useAiDetectionSocket } from '@/app/ContentTools/hooks/useAiDetectionSocket';
import '@/assets/ai-content.css';

type BreadcrumbItem = {
  text: string;
  href: string;
};

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type DetailsTabId =
  | 'overview'
  | 'content-analysis'
  | 'ai-detector'
  | 'eeat-analysis'
  | 'geo-analysis';

const getTabFromPath = (pathname: string): DetailsTabId => {
  if (pathname.includes('/ai-analysis/seo/')) return 'content-analysis';
  if (pathname.includes('/ai-analysis/detector/')) return 'ai-detector';
  if (pathname.includes('/ai-analysis/eeat/')) return 'eeat-analysis';
  if (pathname.includes('/ai-analysis/geo/')) return 'geo-analysis';
  return 'overview';
};

const tabTitle: Record<DetailsTabId, string> = {
  overview: 'نمای کلی',
  'content-analysis': 'تحلیل سئو',
  'ai-detector': 'تشخیص AI',
  'eeat-analysis': 'تحلیل EEAT',
  'geo-analysis': 'تحلیل GEO'
};

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: ReactNode }) => (
    <Link target="_blank" to={href || '#'}>
      {children}
    </Link>
  )
};

const MarkdownReport = ({ message }: { message: string }) => (
  <div
    id="ai-content"
    className="mb-0 fs--1 p-1 text-break"
    style={{
      direction: 'rtl',
      textAlign: 'right'
    }}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      components={markdownComponents}
    >
      {message}
    </ReactMarkdown>
  </div>
);

const AiAnalysisDetails = () => {
  const dispatch = useDispatch();
  const { slug, domain_slug, domain } = useParams();
  const { pathname } = useLocation();
  const domainSlug = domain_slug || '';
  const currentDomain = domain || '';
  const analysisId = slug && slug !== 'last' ? slug : '';
  const activeTab = getTabFromPath(pathname);
  const isContentTab = activeTab === 'content-analysis';
  const isOverviewTab = activeTab === 'overview';
  const isDetectorTab = activeTab === 'ai-detector';
  const isEeatTab = activeTab === 'eeat-analysis';
  const isGeoTab = activeTab === 'geo-analysis';

  const [, setBreadcrumbData] = useOutletContext<OutletContext>();

  const { data, isLoading } = useViewHistoryMessageQuery(analysisId, {
    skip: !analysisId || isOverviewTab || isEeatTab || isGeoTab
  });
  const { data: overviewResponse, isLoading: isOverviewLoading } =
    useGetContentOverviewQuery(
      { id: analysisId },
      { skip: !analysisId || !isOverviewTab }
    );
  const overviewDetectionStatus =
    overviewResponse?.code === 200
      ? overviewResponse.data?.detection_status
      : undefined;
  const overviewEeatStatus =
    overviewResponse?.code === 200
      ? overviewResponse.data?.eeat_status
      : undefined;
  const overviewGeoStatus =
    overviewResponse?.code === 200
      ? overviewResponse.data?.ai_search_status
      : undefined;
  const isDetectionPending =
    overviewDetectionStatus != null && overviewDetectionStatus !== 'finished';
  const isEeatPending =
    overviewEeatStatus != null && overviewEeatStatus !== 'finished';
  const isGeoPending =
    overviewGeoStatus != null && overviewGeoStatus !== 'finished';

  const revalidateAnalysisApis = useCallback(() => {
    if (!analysisId) return;
    dispatch(
      aiContentToolsApi.util.invalidateTags([
        { type: 'AiContentOverview', id: analysisId },
        { type: 'AiDetectionDetails', id: analysisId },
        { type: 'AiEeatDetails', id: analysisId },
        { type: 'AiGeoDetails', id: analysisId }
      ])
    );
  }, [analysisId, dispatch]);

  const overviewWatchStatuses = [
    ...(isDetectionPending ? (['detection_status'] as const) : []),
    ...(isEeatPending ? (['eeat_status'] as const) : []),
    ...(isGeoPending ? (['ai_search_status'] as const) : [])
  ];

  useAiDetectionSocket({
    historyId: analysisId,
    active: Boolean(
      isOverviewTab && analysisId && overviewWatchStatuses.length > 0
    ),
    watchStatuses: [...overviewWatchStatuses],
    onFinished: revalidateAnalysisApis
  });

  useEffect(() => {
    setBreadcrumbData([
      {
        text: 'تاریخچه',
        href: aiAnalysisHistoryPath(domainSlug, currentDomain)
      },
      { text: tabTitle[activeTab], href: '#' }
    ]);
  }, [activeTab, currentDomain, domainSlug, setBreadcrumbData]);

  const overviewData =
    overviewResponse?.code === 200 && overviewResponse.data
      ? mapContentOverview(overviewResponse.data)
      : null;

  return (
    <>
      {!analysisId || isContentTab || isDetectorTab || isEeatTab || isGeoTab ? (
        <AiAnalysisSearchBox
          initialValues={{
            analyzeSeo: isContentTab || isOverviewTab,
            analyzeAi: isDetectorTab || isOverviewTab,
            analyzeEeat: isEeatTab,
            analyzeGeo: isGeoTab || !analysisId
          }}
        />
      ) : null}
      {!analysisId ? (
        <Card>
          <Card.Body className="text-center py-5">
            <p className="text-600 mb-3">
              می‌توانید از فرم بالا یک مورد جدید تحلیل کنید، یا یک گزارش را از
              تاریخچه انتخاب کنید.
            </p>
            <Link
              to={aiAnalysisHistoryPath(domainSlug, currentDomain)}
              className="btn btn-falcon-primary btn-sm"
            >
              ورود به تاریخچه
            </Link>
          </Card.Body>
        </Card>
      ) : isOverviewTab ? (
        isOverviewLoading ? (
          <AiAnalysisOverviewSkeleton />
        ) : overviewData ? (
          <AiAnalysisOverview
            seo={overviewData.seo}
            detector={overviewData.detector}
            eeat={overviewData.eeat}
            geo={overviewData.geo}
            showDetection={overviewData.showDetection}
            isDetectionPending={overviewData.isDetectionPending}
            isEeatPending={overviewData.isEeatPending}
            isGeoPending={overviewData.isGeoPending}
          />
        ) : (
          <Card>
            <Card.Body className="text-center py-5">
              <p className="text-600 mb-0">
                {overviewResponse?.msg || 'در دریافت نمای کلی خطایی رخ داد.'}
              </p>
            </Card.Body>
          </Card>
        )
      ) : isContentTab ? (
        <SeoAnalysisDetailsPanel
          data={data}
          analysisId={analysisId}
          isLoading={isLoading}
          markdownReport={MarkdownReport}
        />
      ) : isDetectorTab ? (
        <AiDetectorDetailsPanel
          analysisId={analysisId}
          domainSlug={domainSlug}
          keyword={data?.keyword}
          markdownReport={MarkdownReport}
        />
      ) : isEeatTab ? (
        <EeatAnalysisDetailsPanel
          analysisId={analysisId}
          markdownReport={MarkdownReport}
        />
      ) : isGeoTab ? (
        <GeoAnalysisDetailsPanel
          analysisId={analysisId}
          markdownReport={MarkdownReport}
        />
      ) : null}
    </>
  );
};

export default AiAnalysisDetails;
